import React, { useEffect, useState } from 'react'
import { Popover } from '@mui/material'
import { useDispatch } from 'react-redux'
import { t } from 'i18next'
import { fetchListWharehouse } from 'redux/actions/clientsActions'
import { useSearchSalesDocumentsContext } from 'state/context/SearchSalesDocumentsContext'
import {
  getDefaultWebSalesOrderWarehouseIds,
  getWebSalesOrderWarehouseIds,
} from './searchWebSalesOrderDocuments.config'

// Survives remounts so the trigger doesn't flash blank while it re-fetches.
const warehousesCache = new Map()

export default function WebSalesOrderWarehouseFilter() {
  const dispatch = useDispatch()
  const { filters, applyFilters, loadingData, errorFormat } =
    useSearchSalesDocumentsContext()
  const [warehouses, setWarehouses] = useState(
    () => warehousesCache.get(filters.companyId) || []
  )
  const [loadingWarehouses, setLoadingWarehouses] = useState(false)
  const [error, setError] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    let active = true
    setAnchorEl(null)
    setWarehouses(warehousesCache.get(filters.companyId) || [])
    setLoadingWarehouses(true)
    setError(false)
    dispatch(
      fetchListWharehouse({
        CompanyId: filters.companyId,
        tenantId: 2,
        SearchText: null,
        PageNumber: 1,
        PageSize: 100,
      })
    )
      .then(branches => {
        if (!active) return
        const unique = new Map()
        for (const branch of branches || []) {
          for (const warehouse of branch.Warehouses || []) {
            if (warehouse.WarehouseId && warehouse.Description) {
              unique.set(warehouse.WarehouseId, warehouse)
            }
          }
        }
        const list = [...unique.values()]
        warehousesCache.set(filters.companyId, list)
        setWarehouses(list)
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoadingWarehouses(false))
    return () => {
      active = false
    }
  }, [dispatch, filters.companyId])

  const openFilter = event => {
    setSelected(getWebSalesOrderWarehouseIds(filters))
    setSearch('')
    setAnchorEl(event.currentTarget)
  }

  const apply = () => {
    applyFilters({ ...filters, warehouseIds: selected, pageNumber: 1 })
    setAnchorEl(null)
  }

  const normalize = value =>
    value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  const visibleWarehouses = warehouses.filter(warehouse =>
    normalize(warehouse.Description).includes(normalize(search))
  )

  const appliedWarehouseIds = getWebSalesOrderWarehouseIds(filters)
  const isAllBranchesSelected = appliedWarehouseIds.length === 0
  const appliedWarehouseNames = warehouses
    .filter(warehouse => appliedWarehouseIds.includes(warehouse.WarehouseId))
    .map(warehouse => warehouse.Description)
  const additionalWarehouseCount = appliedWarehouseNames.length - 1
  const isPlaceholder = !isAllBranchesSelected && !appliedWarehouseNames.length
  const triggerLabel = isAllBranchesSelected
    ? t('SEARCH_QUOTATION_T.ALL_WAREHOUSES_LABEL')
    : appliedWarehouseNames[0] || t('SEARCH_QUOTATION_T.WAREHOUSE_LABEL')

  return (
    <div className="input-group web-orders-warehouse-input-group">
      <button
        type="button"
        className="web-orders-warehouse-trigger"
        data-testid="input-sucursal-filter"
        title={appliedWarehouseNames.join(', ') || undefined}
        onClick={openFilter}>
        <span
          className={`web-orders-warehouse-trigger-text${
            isPlaceholder
              ? ' web-orders-warehouse-trigger-text--placeholder'
              : ''
          }`}>
          {triggerLabel}
        </span>
        {additionalWarehouseCount > 0 && (
          <span
            className="web-orders-warehouse-trigger-badge"
            aria-label={t(
              'SEARCH_QUOTATION_T.ADDITIONAL_WAREHOUSES_ARIA_LABEL',
              {
                count: additionalWarehouseCount,
              }
            )}>
            +{additionalWarehouseCount}
          </span>
        )}
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <div className="web-orders-warehouse-popover">
          <input
            className="web-orders-warehouse-search"
            data-testid="input-search-warehouse"
            aria-label={t('SEARCH_QUOTATION_T.SEARCH_WAREHOUSE_ARIA_LABEL')}
            placeholder={t('SEARCH_QUOTATION_T.SEARCH_WAREHOUSE_PLACEHOLDER')}
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <div className="web-orders-warehouse-options">
            <label>
              <input
                type="checkbox"
                data-testid="checkbox-warehouse-all"
                checked={selected.length === 0}
                onChange={() => setSelected([])}
              />
              {t('SEARCH_QUOTATION_T.ALL_WAREHOUSES_LABEL')}
            </label>
            {loadingWarehouses && warehouses.length === 0 && (
              <p>{t('SEARCH_QUOTATION_T.LOADING_WAREHOUSES_LABEL')}</p>
            )}
            {error && (
              <p>{t('SEARCH_QUOTATION_T.WAREHOUSES_LOAD_ERROR_LABEL')}</p>
            )}
            {visibleWarehouses.map(warehouse => (
              <label key={warehouse.WarehouseId}>
                <input
                  type="checkbox"
                  data-testid={`checkbox-warehouse-${warehouse.WarehouseId}`}
                  checked={selected.includes(warehouse.WarehouseId)}
                  onChange={() =>
                    setSelected(previous =>
                      previous.includes(warehouse.WarehouseId)
                        ? previous.filter(id => id !== warehouse.WarehouseId)
                        : [...previous, warehouse.WarehouseId]
                    )
                  }
                />
                {warehouse.Description}
              </label>
            ))}
          </div>
          <div className="buttons-container">
            <button
              className="btn-clear"
              data-testid="btn-clear-warehouse-filter"
              onClick={() =>
                setSelected(getDefaultWebSalesOrderWarehouseIds(filters))
              }>
              {t('ACTION_TABLE_T.CLEAN_LABEL')}
            </button>
            <button
              className="btn-filter"
              data-testid="btn-apply-warehouse-filter"
              disabled={loadingData || loadingWarehouses || errorFormat}
              onClick={apply}>
              {t('ACTION_TABLE_T.APPLY_LABEL')}
            </button>
          </div>
        </div>
      </Popover>
    </div>
  )
}
